import React, { useState, useEffect, useRef } from "react";
import { FormBlock, CardInputWrapper, Paragraph } from "./styled";
import buttonIconDisabled from "../../assets/images/paywall/icon_button_dis.svg";
import questionIcon from "../../assets/images/paywall/question_icon.svg";
import cvv from "../../assets/images/paywall/cvv_icon.png";
import Button from "./../Button/Button";
import { useOnOutsideClick } from "../../hooks/useOutsideClick";
import axios from "axios";
import {
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useElements,
  useStripe,
  PaymentRequestButtonElement,
} from "@stripe/react-stripe-js";
import { checkValueToType } from "../../utils/validators";
import cardsIcons from "./../../assets/images/common/cards.png";
import dayjs from "dayjs";
import { useDispatch } from "react-redux";
import localeData from "dayjs/plugin/localeData";
import "dayjs/locale/ja";
import { texts } from "../../constants/textsLoaction";
import { API_URL, TRIAL_DAYS } from "../../constants/formConstants";
import { setApplePayAvailible } from "../../store/reducers/payment";

import { baseInputStyle } from "../../constants/formConstants";

dayjs.extend(localeData);

const Form = ({
  country = "default",
  title = "",
  onlyApplePay = false,
  trialCost,
  priceID,
  successAction,
}) => {
  const [isAllFieldsComplete, setAllFieldsComplete] = useState(false);
  const [loading, setLoading] = useState(false);

  // card number validation
  const [isCardNumberCorrect, setCardNumberCorrect] = useState(true);
  const [isCardNumberComplete, setCardNumberComplete] = useState(false);
  const [cardNumberErrorMessage, setCardNumberErrorMessage] = useState("");
  const [cardInputOnFocus, setCardInputOnFocus] = useState(false);
  const [isCardNumberEmpty, setCardNumberEmpty] = useState(true);

  // date validation
  const [isDateCorrect, setDateCorrect] = useState(true);
  const [isDateComplete, setDateComplete] = useState(false);
  const [dateErrorMessage, setDateErrorMessage] = useState("");
  const [isDateEmpty, setDateEmpty] = useState(true);
  const [dateInputOnFocus, setDateInputOnFocus] = useState(false);

  // cvv validation
  const [isCvvCorrect, setCvvCorrect] = useState(true);
  const [isCvvComplete, setCvvComplete] = useState(false);
  const [cvvErrorMessage, setCvvErrorMessage] = useState("");
  const [isCvvEmpty, setCvvEmpty] = useState(true);
  const [cvvInputOnFocus, setCvvInputOnFocus] = useState(false);

  // email validation
  const [email, setEmail] = useState("");
  const [isEmailCorrect, setEmailCorrect] = useState(true);
  const [isEmailComplete, setEmailComplete] = useState(false);
  const [emailErrorMessage, setEmailErrorMessage] = useState("");
  const [emailInputOnFocus, setEmailInputOnFocus] = useState(false);

  const [isCvvPopupOpen, setCvvPopupOpen] = useState(false);

  // common validation error
  const [commonErrorMessage, setCommonErrorMessage] = useState("");

  // applePay
  const [paymentRequest, setPaymentRequest] = useState(null);
  const [paymentRequestKey, setPaymentRequestKey] = useState(0);

  const stripe = useStripe();
  const elements = useElements();

  const dispatch = useDispatch();

  const parentBlock = useRef(null);
  const cardNumberRef = useRef(null);

  const searchParams = new URLSearchParams(window.location.search);

  const clientReferenceId = searchParams.get("client_reference_id");
  const utmSource = searchParams.get("utm_source");
  const utmCampaign = searchParams.get("utm_campaign");

  const options = {
    paymentRequest,
    style: {
      paymentRequestButton: {
        type: "default",
        theme: "dark",
        height: "55px",
      },
    },
  };

  const onErrorTap = () => {
    setCommonErrorMessage("");
  };

  useEffect(() => {
    document.body.addEventListener("touchend", onErrorTap);
    document.body.addEventListener("click", onErrorTap);
    return () => {
      document.body.removeEventListener("touchend", onErrorTap);
      document.body.removeEventListener("click", onErrorTap);
    };
  }, []);

  useEffect(() => {
    setAllFieldsComplete(
      isDateComplete && isCvvComplete && isCardNumberComplete && isEmailComplete
    );
  }, [isDateComplete, isCvvComplete, isCardNumberComplete, isEmailComplete]);

  useEffect(() => {
    if (!stripe || !trialCost) return;

    const amount = Math.round(Number(trialCost) * 100);
    if (!amount || amount <= 0) return;

    const pr = stripe.paymentRequest({
      country: "US",
      currency: "usd",
      total: {
        label: "Get your color",
        amount,
      },
      requestPayerEmail: true,
      requestPayerName: true,
    });

    pr.canMakePayment().then((res) => {
      if (res) {
        dispatch(setApplePayAvailible(true));
        setPaymentRequest(pr); // Монтируем кнопку только если доступна
        setPaymentRequestKey((prev) => prev + 1);
      } else {
        dispatch(setApplePayAvailible(false));
      }
    });

    pr.on("paymentmethod", async (e) => {
      const now = new Date();
      now.setDate(now.getDate() + TRIAL_DAYS);
      const trialEnd = Math.floor(now.valueOf() / 1000);

      const bodyObj = {
        id: e.paymentMethod.id,
        paymentMethodType: e.paymentMethod.id,
        currency: "usd",
        product: priceID,
        client_reference_id: clientReferenceId,
        email: e.payerEmail || "applegoogle@email.com",
        trial_end: trialEnd,
        data: {
          country: "US",
          currency: "usd",
          user_agent: window.navigator.userAgent,
          client_reference_id: clientReferenceId,
          utm_source: utmSource,
          utm_campaign: utmCampaign,
        },
        amount, // Передаём актуальную сумму
      };

      try {
        await axios.post(`${API_URL}/create-payment-intent`, bodyObj);
        e.complete("success");
        successAction();
      } catch (error) {
        console.log("Apple Pay error", error);
        e.complete("fail");
      }
    });
  }, [stripe, trialCost]);

  const onCvvClick = () => {};

  const handleSubmit = async (e, sum) => {
    setLoading(true);
    const { error, paymentMethod } = await stripe.createPaymentMethod({
      type: "card",
      card: elements.getElement(CardNumberElement),
    });
    if (!error) {
      try {
        const { id } = paymentMethod;

        const trialCostComputed = Math.round(Number(trialCost) * 100);

        let meta = {
          currency: "usd",
          client_reference_id: clientReferenceId,
          utm_source: utmSource,
          utm_campaign: utmCampaign,
        };

        let dataObj = {
          amount: trialCostComputed,
          id,
          email,
          data: meta,
        };
        const response = await axios.post(`${API_URL}/payment`, dataObj);
        if (response.data.success) {
          setCommonErrorMessage("");
          handleSubmitSubscription({
            customer: response.data.customer,
          });
        } else {
          setCommonErrorMessage(response.data.message);
          setLoading(false);
        }
      } catch (e) {
        setCommonErrorMessage(e.message);
        setLoading(false);
      }
    } else {
      setCommonErrorMessage(error.message);
      setLoading(false);
    }
  };

  const handleSubmitSubscription = async ({ customer }, onlySubscription) => {
    setLoading(true);
    const now = new Date();
    now.setDate(now.getDate() + 7);
    let mft = Math.floor(now.valueOf() / 1000);
    const priceId = priceID;

    let computedCustomer = customer;

    if (!stripe || !elements) {
      return;
    }

    const result = await stripe.createPaymentMethod({
      type: "card",
      card: elements.getElement(CardNumberElement),
    });

    if (onlySubscription) {
      // Создание клиента
      const customerResponse = await axios.post(`${API_URL}/create-customer`, {
        email: email,
      });
      computedCustomer = customerResponse.data.customer;

      await axios.post(`${API_URL}/attach-payment-method`, {
        customerId: customerResponse.data.customer.id,
        paymentMethodId: result.paymentMethod.id,
      });
    }

    if (result.error) {
      setCommonErrorMessage(result.error.message);
      setLoading(false);
    } else {
      try {
        let dataObj = {
          payment_method: result.paymentMethod.id,
          email,
          product: priceId,
          client_reference_id: clientReferenceId,
          customer: computedCustomer,
          billing_cycle_anchor: mft,
          trial_end: mft,
          currency: "usd",
        };

        const res = await axios.post(`${API_URL}/sub`, dataObj);
        const { client_secret, status } = res.data;

        if (status === "required_action") {
          stripe.confirmCardPayment(client_secret).then(function (result) {
            if (result.error) {
              setCommonErrorMessage(result.error.message);
              setLoading(false);
            } else {
              setCommonErrorMessage("");
              successAction();
              setLoading(false);
            }
          });
        } else {
          setLoading(false);
          successAction();
          console.log("NEED TO SET CALLBACK SUCCESS LOGIC");
        }
      } catch (e) {
        setCommonErrorMessage("Something went wrong");
        setLoading(false);
      }
    }
  };

  const handleCustomInputChange = (
    e,
    setDataFunction,
    setCompleteFunction,
    type
  ) => {
    if (setDataFunction) {
      setDataFunction(e.target.value);
    }
    if (e.target.value.length) {
      checkValueToType(type, e.target.value, null, setCompleteFunction);
    }
    if (e.target.value.length === 0) {
      setEmailErrorMessage("");
    }
  };

  const handleInputChange = (
    e,
    setCorrectFunction,
    setCompleteFunction,
    setErrorMessageFunction,
    setEmpty
  ) => {
    setCorrectFunction(!e.error);

    if (e.error && e.error.message) {
      if (e.empty === false) {
        setEmpty(false);
      } else {
        setEmpty(true);
      }
      setErrorMessageFunction(e.error.message);
    } else {
      if (e.empty === false) {
        setEmpty(false);
      } else {
        setEmpty(true);
      }
      setErrorMessageFunction("");
    }
    setCompleteFunction(e.complete);
  };

  const handleSubmitPayAndSubscriptionClick = async () => {
    if (trialCost !== "0") {
      await handleSubmit();
    } else {
      await handleSubmitSubscription({ customer: null }, true);
    }
  };

  const handleInputFocus = (setFunction) => {
    setFunction(true);
  };

  const handleInputBlur = (setFunction) => {
    setFunction(false);
  };

  const handleCustomInputBlur = (
    setFocusFunction,
    length,
    e,
    setCorrectFunction,
    setCompleteFunction,
    setErrorMessageFunction,
    setDataFunction,
    type
  ) => {
    setFocusFunction(false);
    if (e.target.value.length) {
      checkValueToType(
        type,
        e.target.value,
        setCorrectFunction,
        setCompleteFunction,
        setErrorMessageFunction
      );
    }
  };

  const handleQuestionClick = () => {
    if (isCvvComplete === true) {
      return false;
    }
    const cvvPopupOpen = isCvvPopupOpen;
    setCvvPopupOpen(!cvvPopupOpen);
  };

  const setCardsIcons = () => {
    return cardsIcons;
  };

  const { innerBorderRef } = useOnOutsideClick(() => setCvvPopupOpen(false));

  const formRender = () => {
    return (
      <form id="card-form" autoComplete={"off"}>
        <CardInputWrapper
          className={"cardnumber"}
          isValid={isCardNumberCorrect}
          isComplete={isCardNumberComplete}
          tabIndex={0}
          ref={cardNumberRef}
        >
          <CardInputWrapper.Placeholder
            className={
              isCardNumberEmpty
                ? cardInputOnFocus
                  ? "active"
                  : null
                : "active"
            }
          >
            <Paragraph fw={500} color={"#7E969C"}>
              {texts[country]?.quiz.payment.modal.card_number_placeholder}
            </Paragraph>
          </CardInputWrapper.Placeholder>
          <CardNumberElement
            onFocus={() => handleInputFocus(setCardInputOnFocus)}
            onBlur={() => handleInputBlur(setCardInputOnFocus)}
            onChange={(e) =>
              handleInputChange(
                e,
                setCardNumberCorrect,
                setCardNumberComplete,
                setCardNumberErrorMessage,
                setCardNumberEmpty
              )
            }
            options={{
              style: {
                base: baseInputStyle,
                paddingLeft: 40,
              },
            }}
          />
          {cardNumberErrorMessage ? (
            <CardInputWrapper.Message>
              {cardNumberErrorMessage}
            </CardInputWrapper.Message>
          ) : null}
        </CardInputWrapper>
        <CardInputWrapper.Horizontal style={{ marginBottom: 16 }}>
          <div style={{ width: "49%" }}>
            <CardInputWrapper
              isValid={isDateCorrect}
              isComplete={isDateComplete}
            >
              <CardInputWrapper.Placeholder
                className={
                  isDateEmpty ? (dateInputOnFocus ? "active" : null) : "active"
                }
              >
                <Paragraph fw={500} color={"#7E969C"}>
                  MM/YY
                </Paragraph>
              </CardInputWrapper.Placeholder>
              <CardExpiryElement
                onFocus={() => handleInputFocus(setDateInputOnFocus)}
                onBlur={() => handleInputBlur(setDateInputOnFocus)}
                onChange={(e) =>
                  handleInputChange(
                    e,
                    setDateCorrect,
                    setDateComplete,
                    setDateErrorMessage,
                    setDateEmpty
                  )
                }
                options={{
                  style: {
                    base: baseInputStyle,
                  },
                }}
              />
            </CardInputWrapper>
            {dateErrorMessage ? (
              <CardInputWrapper.Message>
                {dateErrorMessage}
              </CardInputWrapper.Message>
            ) : null}
          </div>
          <div style={{ width: "49%" }}>
            <CardInputWrapper isValid={isCvvCorrect} isComplete={isCvvComplete}>
              <CardInputWrapper.Placeholder
                className={
                  isCvvEmpty ? (cvvInputOnFocus ? "active" : null) : "active"
                }
              >
                <Paragraph fw={500} color={"#7E969C"}>
                  CVC
                </Paragraph>
              </CardInputWrapper.Placeholder>
              <CardCvcElement
                onFocus={() => handleInputFocus(setCvvInputOnFocus)}
                onBlur={() => handleInputBlur(setCvvInputOnFocus)}
                onChange={(e) =>
                  handleInputChange(
                    e,
                    setCvvCorrect,
                    setCvvComplete,
                    setCvvErrorMessage,
                    setCvvEmpty
                  )
                }
                options={{
                  style: {
                    base: baseInputStyle,
                  },
                }}
              />
              <CardInputWrapper.Icon
                className={"cvv"}
                onClick={handleQuestionClick}
              >
                <img src={questionIcon} alt="" />
              </CardInputWrapper.Icon>
              {isCvvPopupOpen ? (
                <CardInputWrapper.Tooltip
                  ref={innerBorderRef}
                  onClick={onCvvClick}
                >
                  <img src={cvv} alt="" style={{ marginBottom: 9 }} />
                  <CardInputWrapper.Tooltip.Arrow />
                </CardInputWrapper.Tooltip>
              ) : null}
            </CardInputWrapper>
            {cvvErrorMessage ? (
              <CardInputWrapper.Message>
                {cvvErrorMessage}
              </CardInputWrapper.Message>
            ) : null}
          </div>
        </CardInputWrapper.Horizontal>
        <CardInputWrapper
          isValid={isEmailCorrect}
          isComplete={isEmailComplete}
          style={{ marginBottom: 16 }}
        >
          <CardInputWrapper.Placeholder
            className={
              !email.length ? (emailInputOnFocus ? "active" : null) : "active"
            }
          >
            <Paragraph fw={500} color={"#7E969C"}>
              Email
            </Paragraph>
          </CardInputWrapper.Placeholder>
          <input
            type="text"
            value={email}
            onFocus={() => handleInputFocus(setEmailInputOnFocus)}
            onBlur={(e) =>
              handleCustomInputBlur(
                setEmailInputOnFocus,
                4,
                e,
                setEmailCorrect,
                setEmailComplete,
                setEmailErrorMessage,
                setEmail,
                "email"
              )
            }
            onChange={(e) =>
              handleCustomInputChange(
                e,
                setEmail,
                setEmailComplete,
                "email-change"
              )
            }
          />
          {emailErrorMessage ? (
            <CardInputWrapper.Message>
              {emailErrorMessage}
            </CardInputWrapper.Message>
          ) : null}
        </CardInputWrapper>
      </form>
    );
  };

  return (
    <div className={``}>
      <FormBlock ref={parentBlock}>
        {title && (
          <Paragraph
            fs={24}
            lh={31}
            fw={600}
            style={{ marginBottom: 10 }}
            className="mt-2"
          >
            {title}
          </Paragraph>
        )}

        <FormBlock.Apple>
          {paymentRequest && (
            <div
              key={paymentRequestKey}
              style={{
                marginBottom: 8,
                overflow: "hidden",
                borderRadius: 8,
              }}
              className={`!relative ${
                loading ? "!pointer-events-none opacity-50" : ""
              }`}
            >
              <PaymentRequestButtonElement
                options={options}
                className="apple-google-pay"
              />
            </div>
          )}
        </FormBlock.Apple>
        {!onlyApplePay && (
          <div>
            <img src={setCardsIcons()} alt="cards" className="mb-4 w-full" />
          </div>
        )}

        <FormBlock.Form>{!onlyApplePay && formRender()}</FormBlock.Form>

        {!onlyApplePay && (
          <div className="mb-4">
            <FormBlock.Button>
              <Button
                buttonStyles={
                  "bg-[#a48256] !font-[800] !text-[18px] text-[#ffffff] mb-4"
                }
                action={(e) => handleSubmitPayAndSubscriptionClick(e)}
                text={texts[country]?.quiz.payment.modal.button_text}
                icon={buttonIconDisabled}
                disabledIcon={buttonIconDisabled}
                isButtonDisabled={!isAllFieldsComplete || loading}
                isLoading={loading}
              />
            </FormBlock.Button>
          </div>
        )}

        {commonErrorMessage ? (
          <CardInputWrapper.Message.Common className={"mb-4"}>
            {commonErrorMessage}
          </CardInputWrapper.Message.Common>
        ) : null}
      </FormBlock>
    </div>
  );
};

export default Form;
